import { useEffect, useState, useCallback } from "react";
import { Link, useLocation, useParams } from "wouter";
import { useAuth } from "@clerk/react";
import {
  useGetContract,
  useUpdateTermSheetStatus,
  useAcceptTermSheetCounter,
  useListTermSheetVersions,
  getGetContractQueryKey,
  getListContractsQueryKey,
  getListTermSheetVersionsQueryKey,
  type TermSheetVersion,
  type TermSheetVersionActor,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { useOrg } from "@/lib/orgContext";
import { useToast } from "@/hooks/use-toast";
import { handleApiError } from "@/lib/handleApiError";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { DemoExportGate } from "@/components/DemoExportGate";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ArrowLeft, ScrollText, Download, Send, ThumbsUp, ThumbsDown, Repeat, ArrowRight, Loader2, ShieldCheck, Info, GitCompare, Pencil } from "lucide-react";
import { TermSheetBadge, type TermSheetStatus } from "@/components/TermSheetBadge";
import { WorkflowStatusBar } from "@/components/WorkflowStatusBar";
import { ApprovalSection } from "@/components/ApprovalSection";
import { PrimaryWorkflowAction } from "@/components/PrimaryWorkflowAction";
import { RouteForApprovalDialog } from "@/components/RouteForApprovalDialog";
import { RecordCounterOfferDialog } from "@/components/RecordCounterOfferDialog";
import { TermSheetCompareDialog } from "@/components/TermSheetCompareDialog";
import { SendExternalDialog } from "@/components/SendExternalDialog";
import { NegotiationRoundsTimeline } from "@/components/NegotiationRoundsTimeline";
import { ReviseTermSheetTermsDialog } from "@/components/ReviseTermSheetTermsDialog";
import type { ContractLifecycleState } from "@/components/LifecycleStateBadge";
import type {
  ApprovalStageId,
  ApprovalStatusLight as WorkflowApprovalStatus,
} from "@/lib/workflowStages";

interface ApprovalStatusLight {
  stage: string;
  status: string;
  isLocked: boolean;
  isExecuted: boolean;
  hasApproval: boolean;
}

async function triggerDownload(url: string, filename: string, token: string | null) {
  const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) throw new Error("Download failed");
  const blob = await res.blob();
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objUrl);
}

function actorInitials(name: string | null | undefined): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "?";
}

function VersionActorPill({ actor }: { actor: TermSheetVersionActor | null | undefined }) {
  const display = actor?.name?.trim() || "Unknown";
  const tooltipBits: string[] = [display];
  if (actor?.role) tooltipBits.push(actor.role);
  if (actor?.personas && actor.personas.length > 0) tooltipBits.push(actor.personas.join(", "));
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center gap-1 align-middle text-xs text-muted-foreground" data-testid="ts-version-actor">
            <Avatar className="h-4 w-4">
              {actor?.imageUrl ? <AvatarImage src={actor.imageUrl} alt={display} /> : null}
              <AvatarFallback className="text-[8px]">{actorInitials(actor?.name)}</AvatarFallback>
            </Avatar>
            <span className="font-normal normal-case">{display}</span>
          </span>
        </TooltipTrigger>
        <TooltipContent side="top">{tooltipBits.join(" · ")}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default function TermSheetDetail() {
  const { id } = useParams<{ id: string }>();
  const termSheetId = parseInt(id, 10);
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { getToken } = useAuth();
  const { org } = useOrg();
  const isAdmin = org?.role === "admin";

  const { data: contract, isLoading } = useGetContract(termSheetId, {
    query: { enabled: !!termSheetId, queryKey: getGetContractQueryKey(termSheetId) },
  });

  // Redirect non-term-sheet contracts to /contracts/:id.
  useEffect(() => {
    if (contract && (contract as { documentKind?: string }).documentKind !== "term_sheet") {
      setLocation(`/contracts/${termSheetId}`, { replace: true });
    }
  }, [contract, termSheetId, setLocation]);

  const updateStatus = useUpdateTermSheetStatus();
  const acceptCounter = useAcceptTermSheetCounter();

  // Version snapshots — internal sends + received counters. Loaded eagerly so
  // the post-accept "side-by-side" view + Compare button have immediate data.
  const { data: versions = [] as TermSheetVersion[] } = useListTermSheetVersions(termSheetId, {
    query: { enabled: !!termSheetId, queryKey: getListTermSheetVersionsQueryKey(termSheetId) },
  });

  const [counterDialogOpen, setCounterDialogOpen] = useState(false);
  const [counterSeed, setCounterSeed] = useState<TermSheetVersion | null>(null);
  const [compareOpen, setCompareOpen] = useState(false);
  const [sendExternalOpen, setSendExternalOpen] = useState(false);
  const [reviseTermsOpen, setReviseTermsOpen] = useState(false);
  const [routeDialogOpen, setRouteDialogOpen] = useState(false);
  const [negotiationRoundsRefreshTrigger, setNegotiationRoundsRefreshTrigger] = useState(0);

  const [approvalStatus, setApprovalStatus] = useState<ApprovalStatusLight | null>(null);
  const refreshApprovalStatus = useCallback(async () => {
    if (!termSheetId) return;
    try {
      const token = await getToken();
      const res = await fetch(`/api/contracts/${termSheetId}/approval`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const approval = data.approval;
        if (approval) {
          setApprovalStatus({
            stage: approval.currentStage,
            status: approval.status,
            isLocked: approval.currentStage === "signature" && approval.status === "in_progress",
            isExecuted: approval.status === "executed",
            hasApproval: true,
          });
        } else {
          setApprovalStatus({ stage: "draft", status: "none", isLocked: false, isExecuted: false, hasApproval: false });
        }
      }
    } catch { /* ignore */ }
  }, [termSheetId, getToken]);

  useEffect(() => { void refreshApprovalStatus(); }, [refreshApprovalStatus]);

  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingPipelinePdf, setExportingPipelinePdf] = useState(false);
  const [promoteOpen, setPromoteOpen] = useState(false);

  if (isLoading || !contract) {
    return (
      <div className="space-y-6 max-w-4xl mx-auto">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-32" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  const ts = contract as unknown as {
    id: number;
    coachId?: number;
    coachName?: string;
    contractType: string;
    salaryRangeMin?: number | null;
    salaryRangeMax?: number | null;
    keyTerms?: string | null;
    termSheetStatus?: TermSheetStatus | null;
    lifecycleState?: ContractLifecycleState;
    negotiationHolder?: string | null;
    deliveryAlertsMutedAt?: string | null;
    bonuses?: Array<{ id: number | string; amount?: number | null; customLabel?: string | null; label?: string | null }>;
  };

  const tsStatus: TermSheetStatus = ts.termSheetStatus ?? "draft";
  const internallyApproved = approvalStatus?.isExecuted === true;
  // Lifecycle: draft -> internally_approved -> sent -> accepted/declined/countered.
  const canMarkInternallyApproved = internallyApproved && tsStatus === "draft";
  const canSend = tsStatus === "internally_approved" || tsStatus === "countered";
  // Outcomes reachable from both sent and countered.
  const canMarkOutcome = tsStatus === "sent" || tsStatus === "countered";
  const canPromote = tsStatus === "accepted";
  const canReviseTerms = isAdmin && tsStatus !== "declined";

  const handleSetStatus = (status: TermSheetStatus) => {
    updateStatus.mutate({ id: termSheetId, data: { status } }, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetContractQueryKey(termSheetId) });
        queryClient.invalidateQueries({ queryKey: getListContractsQueryKey() });
        // The server auto-snapshots an "internal" version on transition to
        // "sent", so the compare dialog count must refresh after every status
        // change. Invalidate unconditionally — cheap and avoids stale UI.
        queryClient.invalidateQueries({ queryKey: getListTermSheetVersionsQueryKey(termSheetId) });
        toast({ title: "Status updated", description: `Term sheet marked as ${status.replace(/_/g, " ")}.` });
      },
      onError: (err) => handleApiError(toast, err, { fallbackTitle: "Could not update status" }),
    });
  };

  // Promote = navigate to wizard prefilled from this term sheet (no auto-create).
  const handlePromote = () => {
    setLocation(`/contracts/new?fromTermSheet=${termSheetId}`);
  };

  const handleAcceptCounter = () => {
    acceptCounter.mutate(
      { id: termSheetId, data: {} },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getGetContractQueryKey(termSheetId) });
          queryClient.invalidateQueries({ queryKey: getListContractsQueryKey() });
          queryClient.invalidateQueries({ queryKey: getListTermSheetVersionsQueryKey(termSheetId) });
          toast({ title: "Counter accepted", description: "Term sheet now reflects the candidate's counter terms." });
        },
        onError: (err) => handleApiError(toast, err, { fallbackTitle: "Could not accept counter" }),
      },
    );
  };

  // Find the most recent counter snapshot and the immediately preceding version
  // (any source) so we can render a side-by-side latest-counter vs prior view
  // when the term sheet is in "countered" status.
  const sortedVersions = [...versions].sort((a, b) => a.versionNumber - b.versionNumber);
  const latestCounter = [...sortedVersions].reverse().find((v) => v.source === "counter") ?? null;
  const priorVersion = latestCounter
    ? sortedVersions.filter((v) => v.versionNumber < latestCounter.versionNumber).slice(-1)[0] ?? null
    : null;

  // Promote requires status=accepted; other states get a disabled-tooltip reason.
  const promoteBlockedReason: string | null =
    tsStatus === "accepted"
      ? null
      : tsStatus === "draft"
        ? "Send this term sheet and have the coach accept it before promoting."
        : tsStatus === "internally_approved"
          ? "Send this term sheet for agreement, then wait for the coach to accept."
          : tsStatus === "sent"
            ? "Waiting for the coach to accept these terms."
            : tsStatus === "countered"
              ? "Resolve the counter-offer (Accepted / Declined) before promoting."
              : tsStatus === "declined"
                ? "Declined term sheets cannot be promoted to a contract."
                : "Term sheet is not ready to be promoted.";

  const salaryMidpoint =
    ts.salaryRangeMin && ts.salaryRangeMax
      ? Math.round((ts.salaryRangeMin + ts.salaryRangeMax) / 2)
      : ts.salaryRangeMax ?? ts.salaryRangeMin ?? null;

  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const token = await getToken();
      const coachLastName =
        ts.coachName?.split(/\s+/).filter(Boolean).pop()?.toLowerCase() ?? "term-sheet";
      const date = new Date().toISOString().split("T")[0];
      const filename = `${coachLastName}-${date}-term-sheet-${termSheetId}.pdf`;
      await triggerDownload(`/api/contracts/${termSheetId}/export.pdf`, filename, token);
    } catch (e) {
      handleApiError(toast, e, { fallbackTitle: "Could not export PDF" });
    } finally {
      setExportingPdf(false);
    }
  };

  // Task #393: candidate-facing per-row "Term sheet pipeline" PDF (cover,
  // sender/recipient, key dates, signature panel) generated by the shared
  // report chrome — separate from the existing compact term-sheet export.
  const handleExportPipelinePdf = async () => {
    setExportingPipelinePdf(true);
    try {
      const token = await getToken();
      const coachLastName =
        ts.coachName?.split(/\s+/).filter(Boolean).pop()?.toLowerCase() ?? "term-sheet";
      const date = new Date().toISOString().split("T")[0];
      const filename = `term-sheet-${termSheetId}-${coachLastName}-${date}.pdf`;
      await triggerDownload(`/api/reports/term-sheets/${termSheetId}.pdf`, filename, token);
    } catch (e) {
      handleApiError(toast, e, { fallbackTitle: "Could not export pipeline PDF" });
    } finally {
      setExportingPipelinePdf(false);
    }
  };

  const lifecycleState: ContractLifecycleState = ts.lifecycleState ?? "draft";
  const signingBonus = (ts.bonuses ?? []).find((b) => (b.label ?? b.customLabel ?? "").toString().toLowerCase().includes("signing"));

  // Adapt local approval shape to ApprovalStatusLight; hasApproval=false ⇒ none/null.
  const workflowApproval: WorkflowApprovalStatus | null = approvalStatus
    ? approvalStatus.hasApproval
      ? {
          hasApproval: true,
          status: approvalStatus.status as WorkflowApprovalStatus["status"],
          currentStage: (approvalStatus.stage as ApprovalStageId | null) ?? null,
        }
      : { hasApproval: false, status: "none", currentStage: null }
    : null;

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" asChild>
            <Link href="/term-sheets"><ArrowLeft className="h-4 w-4" /></Link>
          </Button>
          <div>
            <h1
              className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2 flex-wrap"
              data-testid="text-term-sheet-detail-title"
            >
              <ScrollText className="h-6 w-6 text-purple-600 shrink-0" />
              {ts.coachId && ts.coachName ? (
                <Link
                  href={`/coaches/${ts.coachId}`}
                  className="hover:underline"
                  data-testid="link-term-sheet-coach"
                >
                  {ts.coachName}
                </Link>
              ) : (
                <span>{ts.coachName ?? "Untitled coach"}</span>
              )}
              <span className="text-muted-foreground font-normal">— Term Sheet</span>
              <span
                className="text-xs font-mono text-muted-foreground bg-muted/60 border rounded px-1.5 py-0.5"
                title={`Term sheet ID #${ts.id}`}
              >
                #{ts.id}
              </span>
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <TermSheetBadge status={tsStatus} />
              <span className="text-sm text-muted-foreground capitalize">{ts.contractType.replace(/_/g, " ")}</span>
            </div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <PrimaryWorkflowAction
            contractId={termSheetId}
            lifecycleState={lifecycleState}
            approval={workflowApproval}
            isAdmin={isAdmin}
            onRouteForApproval={() => setRouteDialogOpen(true)}
            onSendExternal={() => setSendExternalOpen(true)}
            basePath="/term-sheets"
            documentKind="term_sheet"
            termSheetStatus={tsStatus}
          />
          <DemoExportGate label="Export PDF" variant="outline" size="default">
            <Button variant="outline" onClick={handleExportPdf} disabled={exportingPdf} data-testid="button-export-term-sheet-pdf">
              {exportingPdf ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              Export PDF
            </Button>
          </DemoExportGate>
          <DemoExportGate label="Pipeline PDF" variant="outline" size="default">
            <Button
              variant="outline"
              onClick={handleExportPipelinePdf}
              disabled={exportingPipelinePdf}
              data-testid="button-export-term-sheet-pipeline-pdf"
              title="Candidate-facing PDF with cover, parties, key dates, and signature panel"
            >
              {exportingPipelinePdf ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              Pipeline PDF
            </Button>
          </DemoExportGate>
        </div>
      </div>

      {/* Sticky promote-to-contract CTA (admin only). */}
      {isAdmin && (
        <div
          className="sticky top-0 z-30 -mx-4 px-4 py-2 sm:mx-0 sm:px-0 sm:py-0"
          data-testid="sticky-promote-bar"
        >
          <div className="rounded-lg border border-purple-200 bg-purple-50/95 backdrop-blur supports-[backdrop-filter]:bg-purple-50/80 px-4 py-3 flex items-center justify-between gap-3 shadow-sm">
            <div className="flex items-start gap-2 min-w-0">
              <ArrowRight className="h-5 w-5 text-purple-700 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-semibold text-purple-900 truncate">
                  {canPromote ? "Ready to promote to a contract" : "Promote to Contract"}
                </div>
                <p className="text-xs text-purple-800 mt-0.5 truncate">
                  {canPromote
                    ? "The coach has accepted these terms. Open the contract wizard prefilled from this term sheet."
                    : promoteBlockedReason}
                </p>
              </div>
            </div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span tabIndex={0} className="shrink-0">
                    <Button
                      onClick={() => setPromoteOpen(true)}
                      disabled={!canPromote}
                      className="bg-purple-600 hover:bg-purple-700 text-white"
                      data-testid="button-promote-to-contract"
                    >
                      <ArrowRight className="h-4 w-4 mr-2" />
                      Promote to Contract
                    </Button>
                  </span>
                </TooltipTrigger>
                {!canPromote && (
                  <TooltipContent side="bottom" className="max-w-xs">
                    {promoteBlockedReason}
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      )}

      <WorkflowStatusBar
        contractId={termSheetId}
        lifecycleState={lifecycleState}
        approval={workflowApproval}
        basePath="/term-sheets"
        documentKind="term_sheet"
        termSheetStatus={tsStatus}
      />

      {/* Read-only banner for non-admins after acceptance. */}
      {canPromote && !isAdmin && (
        <div
          className="rounded-lg border border-purple-200 bg-purple-50 p-4 flex items-start gap-3"
          data-testid="banner-ready-to-promote-readonly"
        >
          <ArrowRight className="h-5 w-5 text-purple-700 mt-0.5 shrink-0" />
          <div className="text-sm text-purple-900">
            <div className="font-semibold">Ready to promote to a contract</div>
            <p className="text-xs text-purple-800 mt-0.5">
              The coach has accepted these terms. An admin can now promote this term sheet to a draft contract.
            </p>
          </div>
        </div>
      )}

      {/* Promote confirmation modal with carry-over preview. */}
      <AlertDialog open={promoteOpen} onOpenChange={setPromoteOpen}>
        <AlertDialogContent data-testid="dialog-promote-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Promote to Contract</AlertDialogTitle>
            <AlertDialogDescription>
              This opens the contract creation wizard prefilled from this term sheet. No contract is created until you submit the wizard.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="rounded-md border bg-muted/30 p-3 text-sm space-y-2">
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Carries over
            </div>
            <dl className="grid grid-cols-[7rem_1fr] gap-y-1.5 gap-x-3 text-sm">
              <dt className="text-muted-foreground">Coach</dt>
              <dd className="font-medium truncate" data-testid="promote-preview-coach">
                {ts.coachName ?? <span className="italic text-muted-foreground">—</span>}
              </dd>
              <dt className="text-muted-foreground">Position</dt>
              <dd className="capitalize">{ts.contractType.replace(/_/g, " ")}</dd>
              <dt className="text-muted-foreground">Salary</dt>
              <dd>
                {salaryMidpoint != null ? (
                  <>
                    <span className="font-medium">${salaryMidpoint.toLocaleString()}</span>
                    {ts.salaryRangeMin && ts.salaryRangeMax && ts.salaryRangeMin !== ts.salaryRangeMax && (
                      <span className="text-xs text-muted-foreground ml-1">
                        (midpoint of ${ts.salaryRangeMin.toLocaleString()}–${ts.salaryRangeMax.toLocaleString()})
                      </span>
                    )}
                  </>
                ) : (
                  <span className="italic text-muted-foreground">TBD — you'll set this in the wizard</span>
                )}
              </dd>
              <dt className="text-muted-foreground">Signing bonus</dt>
              <dd>
                {signingBonus?.amount
                  ? <span className="font-medium">${signingBonus.amount.toLocaleString()}</span>
                  : <span className="italic text-muted-foreground">None</span>}
              </dd>
              <dt className="text-muted-foreground">Key terms</dt>
              <dd className="text-xs text-muted-foreground">
                {ts.keyTerms
                  ? <span>Copied to the contract's notes for reference.</span>
                  : <span className="italic">No key terms recorded.</span>}
              </dd>
            </dl>
          </div>

          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 flex items-start gap-2 text-xs text-amber-900">
            <Info className="h-4 w-4 shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold mb-0.5">Still required in the wizard</div>
              <ul className="list-disc list-inside space-y-0.5">
                <li>Start &amp; end dates</li>
                <li>Final annual salary (if not set above)</li>
                <li>Buyout clause &amp; termination conditions</li>
                <li>Bonus details &amp; performance triggers</li>
              </ul>
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-promote-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handlePromote}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="button-promote-confirm"
            >
              <ArrowRight className="h-4 w-4 mr-2" />
              Open Contract Wizard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
          <div>
            <CardTitle className="text-base">Headline Terms</CardTitle>
            <CardDescription>The non-binding offer summary shared with the coach's representative.</CardDescription>
          </div>
          {canReviseTerms && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setReviseTermsOpen(true)}
              data-testid="button-revise-term-sheet-terms"
            >
              <Pencil className="h-3.5 w-3.5 mr-1.5" /> Revise Terms
            </Button>
          )}
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="rounded-lg bg-muted/40 px-3 py-2">
            <div className="text-xs text-muted-foreground uppercase tracking-wider">Salary Range</div>
            <div className="font-semibold mt-1 kpi-value">
              {ts.salaryRangeMin && ts.salaryRangeMax
                ? `$${ts.salaryRangeMin.toLocaleString()} – $${ts.salaryRangeMax.toLocaleString()}`
                : ts.salaryRangeMax
                  ? `Up to $${ts.salaryRangeMax.toLocaleString()}`
                  : ts.salaryRangeMin
                    ? `From $${ts.salaryRangeMin.toLocaleString()}`
                    : <span className="text-muted-foreground">TBD</span>}
            </div>
          </div>
          <div className="rounded-lg bg-muted/40 px-3 py-2">
            <div className="text-xs text-muted-foreground uppercase tracking-wider">Signing Bonus</div>
            <div className="font-semibold mt-1 kpi-value">
              {signingBonus?.amount
                ? `$${signingBonus.amount.toLocaleString()}`
                : <span className="text-muted-foreground">—</span>}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
          <CardTitle className="text-base">Key Terms</CardTitle>
          {canReviseTerms && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setReviseTermsOpen(true)}
              data-testid="button-revise-key-terms"
            >
              <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {ts.keyTerms ? (
            <pre className="text-sm whitespace-pre-wrap font-sans leading-relaxed">{ts.keyTerms}</pre>
          ) : (
            <p className="text-sm text-muted-foreground italic">No key terms recorded yet.</p>
          )}
        </CardContent>
      </Card>

      {/* Internal approval. Anchor id="approval" matches workflow status bar. */}
      <div id="approval" data-testid="ts-approval-section">
        <ApprovalSection contractId={termSheetId} onApprovalChange={refreshApprovalStatus} />
      </div>

      {/* Shared external-send channel (same rounds as contracts). */}
      <div id="negotiation" className="space-y-4" data-testid="ts-negotiation-section">
        <NegotiationRoundsTimeline
          contractId={termSheetId}
          refreshTrigger={negotiationRoundsRefreshTrigger}
          isAdmin={isAdmin}
          deliveryAlertsMutedAt={ts.deliveryAlertsMutedAt ?? null}
          onDeliveryAlertsMutedChange={() => {
            queryClient.invalidateQueries({ queryKey: getGetContractQueryKey(termSheetId) });
          }}
          lifecycleState={lifecycleState}
          negotiationHolder={ts.negotiationHolder ?? null}
        />
      </div>

      {isAdmin && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Lifecycle Actions</CardTitle>
            <CardDescription>
              Complete internal approval, send for agreement via share link, then mark the outcome
              when the coach responds. Use Revise Terms to change our offer; Record Counter logs
              theirs. Promote unlocks after Accepted.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {!internallyApproved && tsStatus === "draft" && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                Submit and complete the internal approval above before sending this term sheet.
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                className="text-blue-700 border-blue-300 hover:bg-blue-50"
                disabled={!canMarkInternallyApproved || updateStatus.isPending}
                onClick={() => handleSetStatus("internally_approved")}
                data-testid="button-mark-internally-approved"
              >
                <ShieldCheck className="h-4 w-4 mr-2" /> Mark Internally Approved
              </Button>
              {canReviseTerms && (
                <Button
                  variant="outline"
                  onClick={() => setReviseTermsOpen(true)}
                  data-testid="button-lifecycle-revise-terms"
                >
                  <Pencil className="h-4 w-4 mr-2" /> Revise Terms
                </Button>
              )}
              <Button
                variant="default"
                disabled={!canSend}
                onClick={() => setSendExternalOpen(true)}
                data-testid="button-send-term-sheet"
              >
                <Send className="h-4 w-4 mr-2" /> Send for Agreement
              </Button>

              {/* Outcomes available while waiting on the candidate (status=sent). */}
              {tsStatus === "sent" && (
                <>
                  <Button
                    variant="outline"
                    className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                    disabled={updateStatus.isPending}
                    onClick={() => handleSetStatus("accepted")}
                    data-testid="button-mark-accepted"
                  >
                    <ThumbsUp className="h-4 w-4 mr-2" /> Accepted
                  </Button>
                  <Button
                    variant="outline"
                    className="text-amber-700 border-amber-300 hover:bg-amber-50"
                    disabled={updateStatus.isPending}
                    onClick={() => { setCounterSeed(null); setCounterDialogOpen(true); }}
                    data-testid="button-mark-countered"
                  >
                    <Repeat className="h-4 w-4 mr-2" /> Record Counter…
                  </Button>
                  <Button
                    variant="outline"
                    className="text-destructive border-destructive/30 hover:bg-destructive/5"
                    disabled={updateStatus.isPending}
                    onClick={() => handleSetStatus("declined")}
                    data-testid="button-mark-declined"
                  >
                    <ThumbsDown className="h-4 w-4 mr-2" /> Declined
                  </Button>
                </>
              )}

              {/* Three-action resolution available once a counter is on file. */}
              {tsStatus === "countered" && (
                <>
                  <Button
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    disabled={acceptCounter.isPending || !latestCounter}
                    onClick={handleAcceptCounter}
                    data-testid="button-accept-counter"
                  >
                    {acceptCounter.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    <ThumbsUp className="h-4 w-4 mr-2" /> Accept Counter
                  </Button>
                  <Button
                    variant="outline"
                    className="text-amber-700 border-amber-300 hover:bg-amber-50"
                    disabled={updateStatus.isPending}
                    onClick={() => { setCounterSeed(latestCounter); setCounterDialogOpen(true); }}
                    data-testid="button-counter-again"
                  >
                    <Repeat className="h-4 w-4 mr-2" /> Counter Again…
                  </Button>
                  <Button
                    variant="outline"
                    className="text-destructive border-destructive/30 hover:bg-destructive/5"
                    disabled={updateStatus.isPending}
                    onClick={() => handleSetStatus("declined")}
                    data-testid="button-decline-countered"
                  >
                    <ThumbsDown className="h-4 w-4 mr-2" /> Declined
                  </Button>
                </>
              )}
            </div>

            {sortedVersions.length >= 1 && (
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  onClick={() => setCompareOpen(true)}
                  data-testid="button-open-ts-compare"
                >
                  <GitCompare className="h-4 w-4 mr-2" /> Compare Versions ({sortedVersions.length})
                </Button>
              </div>
            )}

            {/* Side-by-side latest counter vs prior version, only while in countered. */}
            {tsStatus === "countered" && latestCounter && (
              <div className="border rounded-md mt-3 overflow-hidden" data-testid="latest-counter-vs-prior">
                <div className="grid grid-cols-[1fr_1fr_1fr] gap-2 py-2 px-2 bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground border-b">
                  <div>Field</div>
                  <div className="text-center space-y-1">
                    <div>
                      {priorVersion
                        ? `${priorVersion.source === "internal" ? "Sent" : "Counter"} v${priorVersion.versionNumber}`
                        : "Prior"}
                    </div>
                    {priorVersion && <VersionActorPill actor={priorVersion.actor} />}
                  </div>
                  <div className="text-center space-y-1">
                    <div className="text-amber-700">Counter v{latestCounter.versionNumber}</div>
                    <VersionActorPill actor={latestCounter.actor} />
                  </div>
                </div>
                {[
                  { label: "Status", k: "status" as const, money: false },
                  { label: "Salary range min", k: "salaryRangeMin" as const, money: true },
                  { label: "Salary range max", k: "salaryRangeMax" as const, money: true },
                  { label: "Signing bonus", k: "signingBonus" as const, money: true },
                  { label: "Key terms", k: "keyTerms" as const, money: false },
                ].map(({ label, k, money }) => {
                  const a = priorVersion?.snapshot[k] ?? null;
                  const b = latestCounter.snapshot[k] ?? null;
                  const differs = (a ?? "") !== (b ?? "");
                  const fmt = (v: unknown) => {
                    if (v == null || v === "") return <span className="italic text-muted-foreground">—</span>;
                    if (money && typeof v === "number") return "$" + v.toLocaleString();
                    return String(v);
                  };
                  return (
                    <div
                      key={k}
                      className={`grid grid-cols-[1fr_1fr_1fr] gap-2 py-2 px-2 text-sm border-b last:border-0 ${differs ? "bg-amber-50/40" : ""}`}
                    >
                      <div className="text-muted-foreground font-medium">{label}</div>
                      <div className={money ? "text-center font-mono" : "text-xs whitespace-pre-wrap"}>{fmt(a)}</div>
                      <div className={`${money ? "text-center font-mono" : "text-xs whitespace-pre-wrap"} ${differs ? "font-semibold" : ""}`}>
                        {fmt(b)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Promote CTA is in the sticky bar above. */}
          </CardContent>
        </Card>
      )}

      {/* After candidate accept — promote is the next step (signature lives on the contract). */}
      {tsStatus === "accepted" && (
        <Card data-testid="card-ts-accepted-next">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ArrowRight className="h-5 w-5 text-purple-600" /> Next: promote to contract
            </CardTitle>
            <CardDescription>
              These terms are agreed. Promote to open the contract wizard — full negotiation and
              e-signature happen on the contract, not on this term sheet.
            </CardDescription>
          </CardHeader>
          {isAdmin && (
            <CardContent>
              <Button
                onClick={() => setPromoteOpen(true)}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="button-promote-from-accepted-card"
              >
                <ArrowRight className="h-4 w-4 mr-2" /> Promote to Contract
              </Button>
            </CardContent>
          )}
        </Card>
      )}

      <ReviseTermSheetTermsDialog
        open={reviseTermsOpen}
        onOpenChange={setReviseTermsOpen}
        termSheetId={termSheetId}
        current={{
          salaryRangeMin: ts.salaryRangeMin ?? null,
          salaryRangeMax: ts.salaryRangeMax ?? null,
          signingBonus: signingBonus?.amount ?? null,
          keyTerms: ts.keyTerms ?? null,
          bonuses: ts.bonuses ?? [],
        }}
        onSaved={() => setNegotiationRoundsRefreshTrigger((n) => n + 1)}
      />

      <RecordCounterOfferDialog
        open={counterDialogOpen}
        onOpenChange={setCounterDialogOpen}
        termSheetId={termSheetId}
        current={{
          contractType: ts.contractType,
          salaryRangeMin: ts.salaryRangeMin ?? null,
          salaryRangeMax: ts.salaryRangeMax ?? null,
          signingBonus: signingBonus?.amount ?? null,
          keyTerms: ts.keyTerms ?? null,
        }}
        seedFromVersion={counterSeed}
      />

      <TermSheetCompareDialog
        open={compareOpen}
        onOpenChange={setCompareOpen}
        termSheetId={termSheetId}
      />

      <SendExternalDialog
        open={sendExternalOpen}
        onOpenChange={setSendExternalOpen}
        contractId={termSheetId}
        basePath="/term-sheets"
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: getGetContractQueryKey(termSheetId) });
          queryClient.invalidateQueries({ queryKey: getListContractsQueryKey() });
          queryClient.invalidateQueries({ queryKey: getListTermSheetVersionsQueryKey(termSheetId) });
          setNegotiationRoundsRefreshTrigger((n) => n + 1);
          void refreshApprovalStatus();
          setSendExternalOpen(false);
          requestAnimationFrame(() =>
            document.getElementById("negotiation")?.scrollIntoView({ behavior: "smooth" }),
          );
        }}
      />

      {isAdmin && (
        <RouteForApprovalDialog
          open={routeDialogOpen}
          onOpenChange={setRouteDialogOpen}
          contractId={termSheetId}
          hasComplianceBlockers={false}
          isAmendment={approvalStatus?.status !== "none" && approvalStatus?.hasApproval === true}
          onSubmitted={() => {
            queryClient.invalidateQueries({ queryKey: getGetContractQueryKey(termSheetId) });
            void refreshApprovalStatus();
          }}
        />
      )}
    </div>
  );
}
